/**
 * AdminMarketplaceSettings — Configuration métier sessions humaines (Iter384 — Lot A)
 *
 * Pilote les paramètres critiques du workflow :
 *  • Durée minimale d'échange (preuve de présence)
 *  • Fenêtre de régénération du rapport
 *  • Nombre maximal de régénérations
 *  • Marge post-session (accès visio)
 *
 * Toute modification est tracée côté backend (logger.info avec email admin).
 * Design : ivoire/or/charbon — strictement aligné AdminCategoryNav.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, RotateCcw, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';

const API = getApiUrl();

const FIELDS = [
  {
    key: 'min_session_duration_seconds',
    label: 'Durée minimale d\'échange',
    unit: 'secondes',
    hint: 'Overlap requis entre coach et coaché avant la génération du rapport (preuve de présence).',
    min: 0, max: 7200, step: 30,
  },
  {
    key: 'report_regeneration_window_hours',
    label: 'Fenêtre de régénération',
    unit: 'heures',
    hint: 'Délai après la 1ʳᵉ génération pendant lequel une régénération reste possible.',
    min: 0, max: 720, step: 1,
  },
  {
    key: 'max_report_regenerations',
    label: 'Régénérations max',
    unit: 'fois',
    hint: 'Nombre maximal de régénérations autorisées par rapport (1 par défaut).',
    min: 0, max: 10, step: 1,
  },
  {
    key: 'session_end_grace_minutes',
    label: 'Marge post-session (accès visio)',
    unit: 'minutes',
    hint: 'Temps après la fin de session pendant lequel la salle visio reste accessible.',
    min: 0, max: 240, step: 5,
  },
];

export default function AdminMarketplaceSettings() {
  const { token, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API}/admin/marketplace/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Accès refusé');
        const data = await res.json();
        setSettings(data);
        setDraft(data);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const dirty = settings && FIELDS.some(f => Number(draft[f.key]) !== Number(settings[f.key]));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(f => {
        if (Number(draft[f.key]) !== Number(settings[f.key])) {
          payload[f.key] = Number(draft[f.key]);
        }
      });
      const res = await fetch(`${API}/admin/marketplace/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setSettings(data);
      setDraft(data);
      toast.success('Paramètres sauvegardés.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setDraft(settings);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF9F3] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#92710A] animate-spin" />
      </div>
    );
  }
  if (!settings) {
    return (
      <div className="min-h-screen bg-[#FBF9F3] flex items-center justify-center text-[#1F2937]">
        Impossible de charger les paramètres.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F3]" data-testid="admin-marketplace-settings-page">
      {/* Header sticky */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-[#E8E2D2]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link
            to="/admin/modules"
            data-testid="admin-marketplace-back-btn"
            className="inline-flex items-center gap-1.5 text-[#6B5E48] text-sm hover:text-[#1F2937] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Retour Dashboard
          </Link>
          <span className="text-[#A89F8A] text-xs">·</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#92710A] font-semibold">
            Administration · Marketplace
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-light text-[#1F1A12] mb-1" style={{ fontFamily: 'Georgia, serif' }}>
            Paramètres Marketplace
          </h1>
          <p className="text-sm text-[#6B5E48]">
            Pilote les seuils métier du workflow des sessions humaines (preuve de présence, régénération du rapport, accès visio).
          </p>
        </div>

        {/* Audit banner */}
        <div className="flex items-center gap-3 bg-[#FFFCEC] border border-[#E8DDB5] rounded-xl px-4 py-3">
          <ShieldCheck className="w-4 h-4 text-[#92710A] flex-shrink-0" />
          <p className="text-xs text-[#6B5E48]">
            Modifications tracées côté serveur · ID admin : <span className="font-semibold text-[#1F2937]">{user?.email || '—'}</span>
          </p>
        </div>

        {/* Settings form */}
        <div className="bg-white border border-[#E8E2D2] rounded-2xl divide-y divide-[#E8E2D2]">
          {FIELDS.map(f => (
            <div key={f.key} className="px-5 py-5">
              <label htmlFor={f.key} className="block">
                <span className="text-sm font-semibold text-[#1F1A12]">{f.label}</span>
                <p className="text-xs text-[#6B5E48] mt-1 mb-3 leading-relaxed">{f.hint}</p>
                <div className="flex items-center gap-3">
                  <input
                    id={f.key}
                    data-testid={`marketplace-setting-${f.key}`}
                    type="number"
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={draft[f.key] ?? ''}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                    className="w-32 px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-[#1F1A12] text-sm tabular-nums focus:outline-none focus:border-[#92710A] focus:bg-white"
                  />
                  <span className="text-sm text-[#6B5E48]">{f.unit}</span>
                  {settings[f.key] != null && (
                    <span className="text-[11px] text-[#A89F8A] ml-auto">
                      Actuel : <span className="font-semibold text-[#1F2937]">{settings[f.key]}</span>
                    </span>
                  )}
                </div>
              </label>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            data-testid="marketplace-settings-reset"
            type="button"
            onClick={handleReset}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium border border-[#E8E2D2] text-[#6B5E48] hover:bg-white disabled:opacity-40"
          >
            <RotateCcw className="w-4 h-4" /> Annuler
          </button>
          <button
            data-testid="marketplace-settings-save"
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1F1A12] shadow-[0_2px_8px_rgba(180,150,40,0.25)] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sauvegarder
          </button>
        </div>
      </main>
    </div>
  );
}
